import { useEffect, useRef } from 'react'
import { useSupabaseAuth } from '../lib/useSupabaseAuth.jsx'
import { useStripeAccountStatus } from '../hooks/useStripeAccountStatus'
import { useNotifications } from './NotificationSystem'

/**
 * Component that monitors Stripe Connect account status changes
 * and shows notifications when the account verification status changes
 */
export const StripeAccountStatusMonitor = () => {
  const { companyId } = useSupabaseAuth()
  const { showSuccess } = useNotifications()
  const previousStatusRef = useRef(null)
  const hasShownNotificationRef = useRef(false)

  const { accountStatus } = useStripeAccountStatus(companyId, {
    enabled: !!companyId,
  })

  // Watch the React Query-backed status for the pending → active transition
  // and surface a single "verified" toast when it happens.
  useEffect(() => {
    if (!accountStatus || !companyId) return

    const wasPending = previousStatusRef.current?.charges_enabled === false
      && previousStatusRef.current?.details_submitted === true
    const isNowActive = accountStatus.charges_enabled === true
      && accountStatus.details_submitted === true

    if (wasPending && isNowActive && !hasShownNotificationRef.current) {
      showSuccess('Your payment account has been verified! You can now accept payments from clients.', {
        title: 'Payment Account Verified',
        duration: 8000,
        persistent: false,
      })
      hasShownNotificationRef.current = true
    }

    const previousKey = previousStatusRef.current
      ? `${previousStatusRef.current.charges_enabled}-${previousStatusRef.current.details_submitted}`
      : null
    const currentKey = `${accountStatus.charges_enabled}-${accountStatus.details_submitted}`
    if (previousKey && currentKey !== previousKey) {
      hasShownNotificationRef.current = false
    }

    previousStatusRef.current = {
      charges_enabled: accountStatus.charges_enabled,
      details_submitted: accountStatus.details_submitted,
    }
  }, [accountStatus, companyId, showSuccess])

  return null
}

